"use client";

import * as React from "react";
import { useState, useMemo, useCallback } from "react";

import SearchInput from "@stadium/shared/src/components/plasmic/custom-options/category-selector/search-input";
import EmptyState from "@stadium/shared/src/components/plasmic/custom-options/category-selector/empty-state";

export interface SingleCategorySelectorProps {
  value?: string;
  updateValue?: (value: string) => void;
  contextData?: {
    slugs?: Array<{ value: string; label: string }>;
    renderedSlugs?: Array<{ value: string; label: string }>;
    categorySlugs?: Array<{ value: string; label: string }>;
    brandSlugs?: Array<{ value: string; label: string }>;
  };
  SideModal?: React.ComponentType<{
    show: boolean;
    onClose: () => void;
    children: React.ReactNode;
  }>;
  selectorSlugs?: Array<{ value: string; label: string }>;
}

// Common styles
const commonStyles = {
  container: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "4px",
  } as React.CSSProperties,
  button: {
    width: "100%",
    height: "33px",
    background: "#f3f3f2",
    padding: "4px 8px",
    borderRadius: "5px",
    textAlign: "left" as const,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap" as const,
  } as React.CSSProperties,
  modalContent: {
    padding: "12px",
    fontFamily: "sans-serif",
  } as React.CSSProperties,
  categoryList: {
    maxHeight: "400px",
    overflowY: "auto" as const,
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
    padding: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  } as React.CSSProperties,
  noteBox: {
    marginTop: "12px",
    padding: "8px",
    backgroundColor: "#f0f0f0",
    borderRadius: "4px",
    fontSize: "11px",
    color: "#666",
  } as React.CSSProperties,
};

// Category Item Component for Single Selection
const SingleSelectCategoryItem: React.FC<{
  category: { value: string; label: string };
  isSelected: boolean;
  onSelect: (value: string) => void;
}> = ({ category, isSelected, onSelect }) => (
  <button
    onClick={() => onSelect(category.value)}
    style={{
      width: "100%",
      display: "flex",
      alignItems: "center",
      padding: "6px",
      cursor: "pointer",
      borderRadius: "4px",
      backgroundColor: isSelected ? "#f0f7ff" : "#f8f8f8",
      transition: "all 0.2s",
    }}
    onMouseEnter={(e) => {
      if (!isSelected) {
        e.currentTarget.style.backgroundColor = "#f0f0f0";
      }
    }}
    onMouseLeave={(e) => {
      if (!isSelected) {
        e.currentTarget.style.backgroundColor = "#f8f8f8";
      }
    }}
  >
    <span
      style={{
        fontSize: "13px",
        color: "#333",
        userSelect: "none",
        fontWeight: isSelected ? "600" : "400",
      }}
    >
      {category.label}
    </span>
  </button>
);

const SingleCategorySelector: React.FC<SingleCategorySelectorProps> = (props) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const { value, updateValue, contextData, SideModal, selectorSlugs } = props;

  // Wrap availableCategories in useMemo to fix dependency warnings
  const availableCategories = useMemo(() => {
    return selectorSlugs || contextData?.slugs || [];
  }, [contextData?.slugs, selectorSlugs]);

  // Filter categories based on search query
  const filteredCategories = useMemo(() => {
    if (!availableCategories) return [];
    if (!searchQuery.trim()) {
      return availableCategories;
    }
    const query = searchQuery.toLowerCase().trim();
    return availableCategories.filter((category) => category.label.toLowerCase().includes(query));
  }, [availableCategories, searchQuery]);

  // Single selection mode values
  const selectedValue = useMemo(() => {
    return value || "";
  }, [value]);

  const selectedCategory = useMemo(() => {
    if (!availableCategories) return undefined;
    return availableCategories.find((category) => category.value === selectedValue);
  }, [selectedValue, availableCategories]);

  // Handlers
  const handleOpenModal = useCallback(() => {
    setSearchQuery("");
    setOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
    setSearchQuery("");
  }, []);

  const handleSelectCategory = useCallback(
    (categoryValue: string) => {
      updateValue?.(categoryValue);
      setOpen(false);
    },
    [updateValue],
  );

  // Fallback if SideModal is not provided
  if (!SideModal) {
    return (
      <div style={{ padding: "10px", color: "#999", fontSize: "12px" }}>
        SideModal not available
      </div>
    );
  }

  return (
    <div style={commonStyles.container}>
      <button onClick={handleOpenModal} style={commonStyles.button}>
        {selectedCategory ? selectedCategory.label : "Select Category"}
      </button>
      <SideModal show={open} onClose={handleCloseModal}>
        <div style={commonStyles.modalContent}>
          <SearchInput value={searchQuery} onChange={setSearchQuery} />
          <div style={commonStyles.categoryList}>
            {filteredCategories.length === 0 ? (
              <EmptyState
                hasCategories={(availableCategories?.length ?? 0) > 0}
                searchQuery={searchQuery}
              />
            ) : (
              filteredCategories.map((category, index) => {
                const isSelected = selectedValue === category.value;

                return (
                  <SingleSelectCategoryItem
                    key={`${category.value}-${index}`}
                    category={category}
                    isSelected={isSelected}
                    onSelect={handleSelectCategory}
                  />
                );
              })
            )}
          </div>
          <div style={commonStyles.noteBox}>
            <strong>Note:</strong> Select a single category to filter products in the carousel.
          </div>
        </div>
      </SideModal>
    </div>
  );
};

export default SingleCategorySelector;
